import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";

/**
 * Tenant database migrations API — the "Database migrations" tab. Master calls
 * each tenant app's own migrations endpoints; these are the master-side
 * wrappers. There is no rollback here, by design.
 *
 * Mirrors cronApi conventions (Bearer token, APIError, POST endpoints).
 */

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

/** One recorded attempt to migrate one tenant. */
export interface MigrationRunDTO {
  id: number;
  tenant_id: string;
  company_id: number | null;
  ran_at: string;
  ok: number; // 1 | 0
  applied_count: number | null;
  pending_after: number | null;
  http_code: number | null;
  message: string | null;
  ran_by: number | null;
  ran_by_name?: string | null;
  duration_ms: number | null;
  created_at: string | null;
  updated_at: string | null;
}

/** One tenant row in the table: registry identity + live status + last run. */
export interface TenantMigrationDTO {
  tenant_id: string;
  company_id: number | null;
  company_name: string | null;
  db_name: string | null;
  status: string;
  reachable: boolean;
  pending: number | null;
  current_version: string | null;
  applied_total: number | null;
  error: string | null;
  last_run: MigrationRunDTO | null;
}

interface ListResponseDTO {
  success: boolean;
  message: string;
  data: { tenants: TenantMigrationDTO[]; checked_at: string };
}

interface RunResponseDTO {
  success: boolean;
  message: string;
  data: { row: MigrationRunDTO | null; skipped: boolean };
}

interface RunAllResponseDTO {
  success: boolean;
  message: string;
  data: {
    rows: MigrationRunDTO[];
    skipped: { tenant_id: string; reason: string }[];
  };
}

interface HistoryResponseDTO {
  success: boolean;
  message: string;
  data: { runs: MigrationRunDTO[] };
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const token = getToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new APIError(
        payload.message || `HTTP error! status: ${response.status}`,
        response.status,
        payload
      );
    }

    return payload as T;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
}

export const listTenantMigrations = async (): Promise<ListResponseDTO> =>
  postJson<ListResponseDTO>("/api/tenant-migrations/list", {});

export const runTenantMigration = async (tenantId: string): Promise<RunResponseDTO> =>
  postJson<RunResponseDTO>("/api/tenant-migrations/run", { tenant_id: tenantId });

export const runAllTenantMigrations = async (): Promise<RunAllResponseDTO> =>
  postJson<RunAllResponseDTO>("/api/tenant-migrations/run-all", {});

export const tenantMigrationHistory = async (
  tenantId: string,
  limit = 20
): Promise<HistoryResponseDTO> =>
  postJson<HistoryResponseDTO>("/api/tenant-migrations/history", {
    tenant_id: tenantId,
    limit,
  });

export { APIError as TenantMigrationAPIError };
